/* istanbul ignore file */
import React from 'react';
import * as ReactDOMClient from 'react-dom/client';
// antd v5's static methods (message/notification/Modal.confirm) still call the
// removed ReactDOM.render; this official patch reroutes them through
// createRoot. Must be imported before any antd component is used.
import '@ant-design/v5-patch-for-react-19';
import mapboxgl from 'mapbox-gl';
import SmartlookClient from 'smartlook-client';
import { shouldUseMirage } from 'utilities/apiConstants';
import * as DatadogManager from './utilities/datadog/datadogManager';
import App from './components/App';
import * as serviceWorker from './serviceWorker';
import applyUrlCropSeasons from './utilities/applyUrlCropSeasons';
import { CaptureLDClient, createLDProvider } from './utilities/launchDarkly';
import '@agconnections/grow-ui/dist/main.css';
import { ProvideAuth, cleanNewUserSession } from './utilities/base-auth';
import { AmplitudeContextProvider } from './utilities/amplitude/useAmplitude';
import { REACT_DOM_CLIENT_GLOBAL } from '../vite-shims/react-dom-legacy-compat';
import './index.css';

// The ui-kit's vendored antd 4 reads createRoot off the `react-dom` namespace,
// where React 19 no longer publishes it. The compat shim aliased over
// `react-dom` forwards here — see vite-shims/react-dom-legacy-compat.js for
// why it can't import react-dom/client itself. Runs before any render, which
// is all antd needs: it captures the function at module init but only invokes
// it when a modal actually opens.
globalThis[REACT_DOM_CLIENT_GLOBAL] = ReactDOMClient;

DatadogManager.initialize();

SmartlookClient.init("2e4e8daf83c4937253a99516982b6482e03be4f5", {
  region: "us",
  version: "nextgen"
});

// One token for every map: the MapboxMap component, the geocoder, and the
// headless maps the PDF exports render. Set before any of them can run.
mapboxgl.accessToken = "pk.eyJ1Ijoic3RyaWRlcmVuZ2luZWVyaW5nIiwiYSI6ImNsdWs5ZG1vNzA2azYyam1scHljMjNyc3kifQ.YBj-8NtAHftqXroxqmbxXw";

async function bootstrap() {
  if (shouldUseMirage) {
    // Dynamic import keeps mirage (and its fixtures) out of the production bundle.
    const { makeServer } = await import('./mirage');
    makeServer({ environment: "qa" });
  }
  cleanNewUserSession(); // if new login on accounts portal, clear session on main app

  // Seed the crop-season selection from ?seasonIds before the breadcrumb mounts
  // (see applyUrlCropSeasons), so an application opened from the product view
  // lands on its own season.
  applyUrlCropSeasons();

  globalThis.__CW_INITIAL_PAGE_LOAD = true;

  // Awaited before the first render so flags are already resolved when screens
  // mount -- see utilities/launchDarkly for why that matters to the API-level
  // flags. Resolves to a no-flags provider rather than rejecting if LaunchDarkly
  // is unreachable, so this can never keep the app from rendering.
  const LDProvider = await createLDProvider();

  const root = ReactDOMClient.createRoot(document.getElementById('root'));
  root.render(
    <AmplitudeContextProvider>
      <ProvideAuth>
        <LDProvider>
          <CaptureLDClient>
            <App />
          </CaptureLDClient>
        </LDProvider>
      </ProvideAuth>
    </AmplitudeContextProvider>
  );

  // If you want your app to work offline and load faster, you can change
  // unregister() to register() below. Note this comes with some pitfalls.
  // Learn more about service workers: https://bit.ly/CRA-PWA

  serviceWorker.unregister();
}

bootstrap().catch(error => {
  // Without this, a failed mirage import (or anything thrown before render)
  // is a silent unhandled rejection and the page stays blank.
  // eslint-disable-next-line no-console
  console.error('App bootstrap failed:', error);
  throw error;
});
